import { open, stat } from 'fs/promises';
import { expandPath } from './paths.js';
import { sep } from 'path';
import { IS_WINDOWS } from './platform.js';

/**
 * Magic numbers for binary executable detection
 */
const MAGIC_NUMBERS = {
  // ELF (Linux)
  ELF: Buffer.from([0x7f, 0x45, 0x4c, 0x46]),
  // Mach-O (macOS) - 32-bit
  MACHO_32: Buffer.from([0xfe, 0xed, 0xfa, 0xce]),
  // Mach-O (macOS) - 64-bit
  MACHO_64: Buffer.from([0xcf, 0xfa, 0xed, 0xfe]),
  // Mach-O (macOS) - Universal binary
  MACHO_UNIVERSAL: Buffer.from([0xca, 0xfe, 0xba, 0xbe]),
  // PE (Windows)
  PE: Buffer.from([0x4d, 0x5a]), // "MZ"
};

/**
 * Script file extensions
 */
const SCRIPT_EXTENSIONS = [
  // Unix shells
  '.sh',
  '.bash',
  '.zsh',
  '.fish',
  // Cross-platform scripting languages
  '.py',
  '.rb',
  '.pl',
  '.js',
  '.ts',
  '.lua',
  '.php',
  '.tcl',
  '.awk',
  '.sed',
  // Windows scripts
  '.ps1',    // PowerShell
  '.psm1',   // PowerShell module
  '.psd1',   // PowerShell data
  '.bat',    // Batch file
  '.cmd',    // Command script
  '.vbs',    // VBScript
  '.wsf',    // Windows Script File
];

/**
 * Windows executable extensions
 */
const WINDOWS_EXECUTABLE_EXTENSIONS = ['.exe', '.com', '.dll'];

/**
 * Check if a buffer starts with a magic number
 */
const bufferStartsWith = (buffer: Buffer, magic: Buffer): boolean => {
  if (buffer.length < magic.length) {
    return false;
  }
  for (let i = 0; i < magic.length; i++) {
    if (buffer[i] !== magic[i]) {
      return false;
    }
  }
  return true;
};

/**
 * Best-effort BINARY detection on an in-memory buffer (issue #100).
 *
 * Used by redacted-checksum drift detection: the secret scanner SKIPS binary
 * files, so their repo copies are NEVER redacted. If a binary happened to
 * contain a stored secret's bytes in its lossy utf-8 decode, redacting it at
 * compare time would produce a checksum that can never match the un-redacted
 * repo copy — fabricating drift that never resolves. Treat a buffer as binary
 * when it starts with a known executable magic number OR contains a NUL byte in
 * its leading bytes (the same NUL heuristic git uses to classify blobs), so such
 * files always hash their RAW bytes.
 */
export const isBinaryBuffer = (buffer: Buffer): boolean => {
  if (
    bufferStartsWith(buffer, MAGIC_NUMBERS.ELF) ||
    bufferStartsWith(buffer, MAGIC_NUMBERS.MACHO_32) ||
    bufferStartsWith(buffer, MAGIC_NUMBERS.MACHO_64) ||
    bufferStartsWith(buffer, MAGIC_NUMBERS.MACHO_UNIVERSAL) ||
    bufferStartsWith(buffer, MAGIC_NUMBERS.PE)
  ) {
    return true;
  }

  // A NUL byte within the inspected window is the classic "this is binary"
  // signal. Bounded to the leading bytes to stay cheap on large files.
  const scanLen = Math.min(buffer.length, 8000);
  for (let i = 0; i < scanLen; i++) {
    if (buffer[i] === 0) {
      return true;
    }
  }
  return false;
};

/**
 * Check if file is an executable binary by reading magic numbers
 */
export const isBinaryExecutable = async (path: string): Promise<boolean> => {
  const expandedPath = expandPath(path);

  try {
    // Check if file exists and get stats
    const stats = await stat(expandedPath);

    // Directories are not binaries
    if (stats.isDirectory()) {
      return false;
    }

    // On Windows, check file extension first since Unix permission bits don't apply
    if (IS_WINDOWS) {
      const lowerPath = expandedPath.toLowerCase();
      if (WINDOWS_EXECUTABLE_EXTENSIONS.some((ext) => lowerPath.endsWith(ext))) {
        return true;
      }
    }

    // Check execute permissions (Unix-like systems)
    // 0o111 = execute bit for owner, group, and others
    // On Windows, this will always be 0, so we rely on file extension above
    const hasExecutePermission = !IS_WINDOWS && (stats.mode & 0o111) !== 0;

    // Read first 512 bytes to check magic numbers
    let fileHandle;
    try {
      fileHandle = await open(expandedPath, 'r');
      const buffer = Buffer.alloc(512);
      await fileHandle.read(buffer, 0, 512, 0);

      // Check for binary magic numbers
      if (
        bufferStartsWith(buffer, MAGIC_NUMBERS.ELF) ||
        bufferStartsWith(buffer, MAGIC_NUMBERS.MACHO_32) ||
        bufferStartsWith(buffer, MAGIC_NUMBERS.MACHO_64) ||
        bufferStartsWith(buffer, MAGIC_NUMBERS.MACHO_UNIVERSAL) ||
        bufferStartsWith(buffer, MAGIC_NUMBERS.PE)
      ) {
        return true;
      }

      // If has execute permission but no magic number, might be a script
      // Check if it's actually a script (has shebang)
      if (hasExecutePermission) {
        const startsWithShebang = buffer[0] === 0x23 && buffer[1] === 0x21; // "#!"
        return !startsWithShebang; // Binary if no shebang
      }

      return false;
    } finally {
      if (fileHandle) {
        await fileHandle.close();
      }
    }
  } catch {
    // If any error occurs while checking the file (e.g. it does not exist,
    // cannot be accessed, or cannot be read), treat it as "not an executable
    // binary" and return false. Callers only rely on the boolean result.
    return false;
  }
};

/**
 * Check if file is a script based on shebang or extension
 */
export const isScriptFile = async (path: string): Promise<boolean> => {
  const expandedPath = expandPath(path);

  try {
    // Check extension first
    const hasScriptExtension = SCRIPT_EXTENSIONS.some((ext) => expandedPath.endsWith(ext));
    if (hasScriptExtension) {
      return true;
    }

    // Check for shebang
    const stats = await stat(expandedPath);
    if (stats.isDirectory()) {
      return false;
    }

    let fileHandle;
    try {
      fileHandle = await open(expandedPath, 'r');
      const buffer = Buffer.alloc(2);
      await fileHandle.read(buffer, 0, 2, 0);

      // Check for shebang "#!"
      return buffer[0] === 0x23 && buffer[1] === 0x21;
    } finally {
      if (fileHandle) {
        await fileHandle.close();
      }
    }
  } catch {
    // Any error while checking is treated as "not a script" to keep detection best-effort.
    // This includes file not found, permission denied, or read errors.
    return false;
  }
};

/**
 * Check if file should be excluded from bin directory tracking
 * Returns true for binary executables in ~/bin or ~/.local/bin
 * Returns false for script files
 */
export const shouldExcludeFromBin = async (path: string): Promise<boolean> => {
  const expandedPath = expandPath(path);

  // Only the user's executable bin roots count. Resolve them to absolute
  // paths and require the file to live *under* one of those exact roots.
  // Matching on the parent directory's basename ('bin') is wrong because it
  // would also exclude unrelated paths like ~/projects/bin/script and would
  // over/under-match variants such as ~/.local/foo-bin.
  const binRoots = [expandPath('~/bin'), expandPath('~/.local/bin')];
  const isInBinDir = binRoots.some((root) => expandedPath.startsWith(root + sep));

  if (!isInBinDir) {
    return false;
  }

  // Check if it's a directory (don't exclude directories themselves)
  try {
    const stats = await stat(expandedPath);
    if (stats.isDirectory()) {
      return false;
    }
  } catch {
    return false;
  }

  // If it's a script, don't exclude it
  if (await isScriptFile(expandedPath)) {
    return false;
  }

  // If it's a binary executable, exclude it
  return await isBinaryExecutable(expandedPath);
};

/**
 * Get a human-readable description of why a file is being excluded
 */
export const getBinaryExclusionReason = async (path: string): Promise<string | null> => {
  if (await shouldExcludeFromBin(path)) {
    return 'Binary executable in bin directory';
  }
  return null;
};

